import React, { Component } from 'react';
import { SORT, POPUP_LIFETIME } from './../../constants';
import popupActions from './../../reusable/MessagePopup/redux/MessagePopup.actions';
import xhr from './../../utils/xhr';
import withInfiniteScroll from './../../reusable/LightTable/hoc/Scrollable';
import withSort from './../../reusable/LightTable/hoc/Sortable';
import withSearch from './../../reusable/LightTable/hoc/Searchable';
import withSelection from './../../reusable/LightTable/hoc/Selectable';
import LightTable from './../../reusable/LightTable/VeryLightTable';
import { compose } from 'redux';
import { merge, find, intersectionWith, differenceWith } from 'lodash';

const Table = compose(
    withSelection,
    withSort,
    withSearch,
    withInfiniteScroll
)(LightTable);

const defaultLoadParams = {
    filters: { query: "" },
    sorting: {
        sortBy: 'name',
        direction: SORT.ASC
    },
    pagination: { pageNumber: 0, pageSize: 10 },
    reset: false
};

export default class TableCoreComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            listItems: null,
            totalCount: null,
            filterCount: null,
            selectedItems: [],
            searchTerm: "",
            activeModal: null,
            filters: null
        };

        this.loadParams = defaultLoadParams;

        this.loadItems = this.loadItems.bind(this);
        this.selectAllToggle = this.selectAllToggle.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.fatalError = this.fatalError.bind(this);
        this.handleErrorState = this.handleErrorState.bind(this);
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
        this.handleChangeSearchTerm = this.handleChangeSearchTerm.bind(this);
        this.shouldRowUpdate = this.shouldRowUpdate.bind(this);
        this.clearSelections = this.clearSelections.bind(this);
    }

    componentDidMount() {
        return this.loadItems();
    }

    /**
     * Main function of changing the load params and actually loading the items
     * @param params
     * @returns {Promise.<TResult>}
     * @public
     */
    loadItems(params, retry = false) {
        this.loadParams = merge({}, this.loadParams, params); // mutates/merges the component loadParams
        const { filters, sorting, reset } = { ...this.loadParams };

        this._manageReset(reset);
        this._manageFilters(filters);

        return this._manageApiCalls(this.loadParams).catch((e) =>{
            this.fatalError(e, retry);
        });
    }

    /**
     * Returns if table is in filtered mode (be it search or other filters)
     * @returns {boolean}
     * @private
     */
    _isFiltered() {
        let isFiltered = false;
        let filters = null
        if (this.getFilters) {
            filters = this.getFilters()
        }
        for (let filterId in this.loadParams.filters) {
            if (filterId !== "query" && filters) {
                filters.forEach(filter => {
                    if (filter.default !== filter.value) {
                        isFiltered = true
                    }
                })
            } else {
                if (this.loadParams.filters.query && this.loadParams.filters.query.length) {
                    isFiltered = true;
                }
            }
        }
        return isFiltered;
    }

    /**
     * Handles "reset" load param
     * @param reset
     * @private
     */
    _manageReset(reset) {
        if (reset) {
            this._resetTableState();
            this.loadParams.reset = false;
        }
    }

    /**
     * Handles the load params filtering or search queries
     * @param filters
     * @private
     */
    _manageFilters(filters) {
        this.setState({ searchTerm: filters.query });
    }

    /**
     * Manages the calls count and items and inserts them into the list
     * @param loadParams
     * @returns {Promise.<TResult>}
     * @private
     */
    _manageApiCalls(loadParams) {
        return Promise.all([
            this._manageApiCountCall(loadParams.filters),
            this._manageApiItemsCall(loadParams)
        ]).then(responses => this._insertItems(...responses, this._isFiltered()))
    }

    /**
     * Individual api call for count
     * @param filters
     * @returns {Request|Promise.<TResult>|*}
     * @private
     */
    _manageApiCountCall(filters) {
        return this.api.count(filters).then(countRes => countRes.body);
    }

    /**
     * Individual api call for the items
     * @param loadParams
     * @returns {Promise.<T>}
     * @private
     */
    _manageApiItemsCall(loadParams) {
        const { filters, sorting, reset } = { ...loadParams };

        return this.api.get(filters, sorting,
            { pageNumber: this.loadParams.pagination.pageNumber, pageSize: this.loadParams.pagination.pageSize })
            .then(response => response.body)
    }

    /**
     * Inserts the items in state and handles the count
     * @param items
     * @param count
     * @param isFiltered
     * @returns {Array}
     * @private
     */
    _insertItems(count, items, isFiltered) {
        const listItems = (this.state.listItems || []).concat(items);
        if (isFiltered) {
            // also call count without filter to see the real total
            this._manageApiCountCall(this.defaultLoadParams.filters).then(totalCount => {
                this._setCountAndItems(listItems, count, totalCount)
            })
        } else {
            this._setCountAndItems(listItems, count, count)
        }

        return listItems;
    }

    _setCountAndItems(listItems, filterCount, totalCount) {
        this.setState({ listItems, filterCount, totalCount })
    }

    /**
     * Empty the list and set the page to 0, for use in this core component
     * @private
     */
    _resetTableState() {
        this.setState({
            listItems: null
        });
    }

    /**
     * A "refresh" of the state, keeping the params intact
     * @public
     */
    reloadTable() {
        this.closeModal();
        this.loadParams.pagination.pageNumber = 0;
        this.loadItems({ reset: true });
    }

    /**
     * Main formatter function. Receives raw item and search query, returns final version of item.
     * Important: keep raw item in "object" field in the processed item, it is used internally
     * @param item - raw item
     * @param searchTerm - search query for highlighting it in table
     * @returns {{object: *}}
     * @public
     */
    formatItem(item, searchTerm = null) {
        return {
            ...item,
            object: item
        };
    }

    /**
     * View function for item in selected box
     * @param item
     * @public
     */
    formatSelectedItem = (item) => <span>{item.name}</span>;

    /**
     * Handler for selection change
     * @param selectedItems
     */
    handleSelectionChange(selectedItems) {
        this.setState({ selectedItems });
    };

    /**
     * Clears all selected items
     */
    clearSelections() {
        this.setState({ selectedItems: [] });
    }

    /**
     * Clears selected items from current view
     */
    clearVisibleSelections() {
        const selectedItems = differenceWith(
            this.state.selectedItems.map(i => i.object),
            this.getVisibleSelectedItems(),
            this.itemIdentifier
        ).map(this.formatItem);
        this.setState({ selectedItems });
    }

    handleChangeSearchTerm(searchTerm) {
        this.setState({ searchTerm })
    }

    /**
     * Toggles select all state
     */
    selectAllToggle() {
        if (this.state.selectedItems.length === this.state.totalCount) {
            this.setState({ selectedItems: [] })
        } else {
            this.api.get(this.defaultLoadParams.filters, this.defaultLoadParams.sorting, { pageNumber: -1, pageSize: -1 }).then(items => {
                this.setState({ selectedItems: items.body.map(item => this.formatItem(item))});
            })
        }
    }

    closeModal() {
        this.setState({ activeModal: null });
    }

    /**
     * Used to get the selected items that are currently in view, usually for applying an action to them
     */
    getVisibleSelectedItems() {
        return intersectionWith(this.state.listItems, this.state.selectedItems.map(i => i.object), this.itemIdentifier);
    }

    /**
     * Resets state in case of error
     */
    handleErrorState(e) {
        console.warn('Error caught:', e);
        this.setState({
            listItems: [],
            totalCount: 0,
            itemCount: 0
        });
    }

    /**
     * When things really go south
     * @param e
     * @param retry - if true, will go to error page
     */
    fatalError(e, retry = false) {
        console.warn('Error caught:', e);

        if(retry) {
            xhr.handleAllErrors(e);
            return;
        }

        popupActions.setMessages({
            popupTitle: null,
            messages: [{
                messageId: 'General_CoreFunctionality_UIText_general.genericErrorMsg',
                messageType: 'system-error'
            }]
        });

        setTimeout(popupActions.removeMessage, POPUP_LIFETIME);

        // attempt retry with default params, reset table state
        this.setState({ selectedItems: [] });
        this.loadParams.pagination.pageNumber = 0;
        this._resetTableState();
        this.loadItems(this.defaultLoadParams, true);
    }

    showSuccessPopup(message, value) {
        // Show received success message.
        popupActions.setMessages({
            popupTitle: null,
            messages:   [
                {
                    messageId:   message,
                    messageType: 'success',
                    values:      value
                }
            ]
        });
        // Hide the message after 5 seconds.
        setTimeout(popupActions.removeMessage, POPUP_LIFETIME);
    }

    /**
     * Generic error message when small things go south,
     */
    showGenericError(e, reload = false) {
        console.error(e);
        if (reload) {
            this.reloadTable();
        }
        popupActions.setMessages({
            popupTitle: null,
            messages: [
                {
                    messageId: 'General_CoreFunctionality_Error_general.saveError',
                    messageType: 'system-error'
                }
            ]
        });

        // Hide the message after 5 seconds.
        setTimeout(popupActions.removeMessage, POPUP_LIFETIME);
    }

    /**
     * Updates the list and selected item with the given "items",
     * using an identifier to match the items (default is by "id" field)
     * @param items
     * @returns updated items
     */
    updateItems(items) {
        if (this.state.listItems) {
            const listItems = this.state.listItems.map(listItem => this._listIdentifier(items, listItem))
            this.setState(state => ({
                listItems,
                selectedItems: state.selectedItems.map(listItem => this.formatItem(this._listIdentifier(items, listItem.object)))
            }))
            return listItems;
        }
    }

    /**
     * Hot Update one of the list items
     * @param item
     * @returns updated list item
     */
    updateItem(item) {
        return this.updateItems([item])[0];
    }

    /**
     * Toggles busy (loading) state of the row
     * @param item
     * @param loadingState
     */
    setItemLoading(item, loadingState) {
        this.setItemsLoading([item], loadingState);
    }

    /**
     * Toggles busy (loading) state of multiple rows
     * @param items
     * @param loadingState
     */
    setItemsLoading(items, loadingState) {
        if (this.state.listItems) {
            this.setState(state => ({
                listItems: state.listItems.map(listItem => {
                    let correspondingListItem = null
                    items.forEach(item => {
                        if (this.itemIdentifier(item, listItem)) {
                            correspondingListItem = { ...listItem, busy: loadingState };
                        }
                    })
                    if (correspondingListItem) {
                        return correspondingListItem;
                    } else {
                        return listItem;
                    }
                })
            }));
        }
    }

    /**
     * Uses itemIdentifier to match & map elements from a list
     * @param list
     * @param item
     * @returns {*}
     * @private
     */
    _listIdentifier(list, item) {
        let result = item;
        list.forEach(listItem => {
            if (this.itemIdentifier(listItem, item)) result = listItem;
        })
        return result;
    }

    /**
     * A function that tells if a row data actually changed. Used for preventing unnecessary re-renders
     * @param prevItem
     * @param nextItem
     * @returns {boolean}
     */
    shouldRowUpdate(prevItem, nextItem) {
        return prevItem.object !== nextItem.object
    }

    /**
     * Used to identify and mapping items by a unique identifier (id), can be overridden in case identifier is different
     * @param a
     * @param b
     * @returns {boolean}
     */
    itemIdentifier = (a, b) =>  a.id === b.id;

    _selectedItemIdentifier = (a, b) => a.object.id === b.id;

    /**
     * (deprecated)
     * When we do changes in the listItems objects, we might want to keep them in sync with selected items, if any
     */
    syncSelectedItems(identifier) {
        this.setState(state => ({ selectedItems: state.selectedItems.map(
            selectedItem => {
                const syncedItem = find(state.listItems, listItem => identifier(selectedItem, listItem));
                if (syncedItem) {
                    return this.formatItem(syncedItem);
                } else {
                    console.error("Cant find element to sync", selectedItem)
                }
            }
        ) }));
    }

    /**
     * Returns all the necessary props for the table
     * @returns {{headerItems, listItems: null, loadItems: TableCoreComponent.loadItems, className: string, totalCount: number, filterCount: number, itemCount: number, defaultSort: (defaultLoadParams.sorting|{sortBy, direction}), onSelectionChange: (function(*)), selectedItems: Array, formatSelectedItem: (function(*): XML), selectAllToggle: TableCoreComponent.selectAllToggle, ref: (function(*): *)}}
     */
    getTableProps() {
        const listItems = this.state.listItems ? this.state.listItems.map(
            item => (
                {
                    ...this.formatItem(item, this.state.searchTerm),
                    updateFunc: [this.shouldRowUpdate] // inject updateFunc - important for re-render optimization
                }
            )) : null

        return {
            headerItems: this.headerItems || {},
            listItems,
            loadItems: this.loadItems,
            className: "selectable-table",
            totalCount: this.state.totalCount,
            filterCount: this.state.filterCount,
            itemCount: this.state.filterCount, // it might look duplicated but it's not, filterCount can change it's value in the HOCs
            onSelectionChange: this.handleSelectionChange,
            selectedItems: this.state.selectedItems,
            onSearchTermChange: this.handleChangeSearchTerm,
            searchTerm: this.state.searchTerm,
            defaultSort: this.defaultLoadParams.sorting,
            loadParams: this.loadParams,
            formatSelectedItem: this.formatSelectedItem,
            selectAllToggle: this.selectAllToggle
        }
    }

    render() {
        return <div className="selectable-table">
            <Table {...this.getTableProps()} />
            {this.state.activeModal && this.state.activeModal}
        </div>
    }
}



// WEBPACK FOOTER //
// ./src/scripts/reusable/LightTable/TableCoreComponent.jsx